import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useAiAssistant } from '@/contexts/AiAssistantContext';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { EmptyState } from '@/components/ai-assistant/chat/EmptyState';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAiAssistantSettings } from '@/hooks/use-ai-assistant-settings';
import { useAuth } from '@/contexts/AuthContext';
import { ChatScrollAreaRef } from '@/components/ai-assistant/chat/ChatScrollArea';
import { MessageContainer } from '@/components/ai-assistant/chat/MessageContainer';
import { InputFooter } from '@/components/ai-assistant/chat/InputFooter';
import { StatusBanner } from '@/components/ai-assistant/chat/StatusBanner';
import { useOnlineStatus } from '@/hooks/use-online-status';
import { usePrefetchQuestions } from '@/hooks/use-prefetch-questions';
import { useEngagementTracking } from '@/hooks/use-engagement-tracking';
import { toast } from 'sonner';
import { MessageOptions } from '@/types/openai';
import { logger } from '@/utils/production-logger';
import { GestureHandler, useGestureShortcuts } from '@/components/ai-assistant/chat/GestureHandler';
import { ConversationBranching } from '@/components/ai-assistant/chat/ConversationBranching';
import { useIsMobile } from '@/hooks/use-mobile';
import { UsageLimitIndicator } from '@/components/ai-assistant/chat/UsageLimitIndicator';

const AiAssistantPage: React.FC = () => {
  const { 
    currentSession, 
    messages, 
    sendMessage, 
    isLoading, 
    isTyping, 
    loadMoreMessages, 
    hasMoreMessages, 
    isLoadingMoreMessages,
    updateMessageFeedback
  } = useAiAssistant();
  
  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  const isOnline = useOnlineStatus();
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const { language } = useLanguage();
  const { user } = useAuth();
  const { 
    ensureUserProfile,
    aiMode,
    updateAiMode
  } = useAiAssistantSettings();
  
  const chatScrollRef = useRef<ChatScrollAreaRef>(null);
  const lastMessageLengthRef = useRef<number>(0);
  const lastNearBottomRef = useRef<boolean>(true);
  const isMobile = useIsMobile();
  const { createGestureProps } = useGestureShortcuts();
  
  const isAiWorking = isLoading || isTyping;
  
  // Mock conversation branches data - in production, this would come from the context
  const [conversationBranches] = useState([
    {
      id: 'main',
      name: 'Main Conversation',
      messages: messages,
      createdAt: new Date()
    }
  ]);
  const [currentBranch] = useState('main');
  
  // Track user engagement - ALWAYS CALL WITH SAFE VALUES
  useEngagementTracking(
    currentSession?.id || null,
    currentSession?.topic || null
  );
  
  // Prefetch questions - ALWAYS CALL WITH SAFE VALUES
  usePrefetchQuestions(
    currentSession?.id || null,
    currentSession?.topic || null,
    isOnline,
    language
  );

  useEffect(() => {
    const safeMessageCount = Array.isArray(messages) ? messages.length : 0;
    
    logger.debug('Messages state updated', {
      totalCount: safeMessageCount,
      sessionId: currentSession?.id,
      isLoading,
      isTyping,
      component: 'AiAssistantPage'
    });
  }, [messages.length, currentSession?.id, isLoading, isTyping]);

  useEffect(() => {
    if (user?.id) {
      ensureUserProfile().catch(error => {
        console.error('Error ensuring user profile exists:', error);
      });
    }
  }, [user?.id, ensureUserProfile]);

  useEffect(() => {
    const currentMessageLength = Array.isArray(messages) ? messages.length : 0;
    const hasStreamingMessage = messages.some(m => m.is_streaming);
    
    if (
      currentMessageLength > 0 && 
      (currentMessageLength !== lastMessageLengthRef.current || hasStreamingMessage) &&
      (isAutoScrollEnabled || isAiWorking)
    ) {
      const scrollTimer = setTimeout(() => {
        if (chatScrollRef.current) {
          chatScrollRef.current.scrollToBottom('smooth');
        }
      }, 100);
      
      lastMessageLengthRef.current = currentMessageLength;
      
      return () => clearTimeout(scrollTimer);
    }
    
    lastMessageLengthRef.current = currentMessageLength;
  }, [messages.length, messages.some(m => m.is_streaming), isAutoScrollEnabled, isAiWorking]);

  // Handle scroll near bottom events
  const handleScrollNearBottom = useCallback((isNear: boolean) => {
    console.log('AiAssistantPage handleScrollNearBottom:', isNear);
    if (isNear !== lastNearBottomRef.current) {
      lastNearBottomRef.current = isNear;
      setIsNearBottom(isNear);
      setIsAutoScrollEnabled(isNear);
    }
  }, []);
  
  const handleScrollNearTop = useCallback(() => {
    // Currently a no-op
  }, []);

  const scrollToBottom = useCallback(() => {
    console.log('AiAssistantPage scrollToBottom called');
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollToBottom();
      setIsNearBottom(true);
      setIsAutoScrollEnabled(true);
    }
  }, []);

  // Usage check with immediate increment - called by MessageInput
  const handleUsageCheck = useCallback(async (topic: 'guide' | 'performance' | 'trading_strategy' | 'advisor'): Promise<boolean> => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return false;
    }

    try {
      console.log('[FRONTEND DEBUG] Starting usage check for:', {
        userId: user.id.substring(0, 8) + '...',
        topic,
        timestamp: new Date().toISOString()
      });

      // Import usage service dynamically to avoid circular dependencies
      const { checkMessageLimit, incrementMessageUsage } = await import('@/services/ai-message-limits-service');
      
      // STEP 1: Check if user can send message
      console.log('[FRONTEND DEBUG] About to check message limit...');
      
      let limitCheck;
      try {
        limitCheck = await checkMessageLimit(user.id, topic);
        console.log('[FRONTEND DEBUG] checkMessageLimit completed successfully:', limitCheck);
      } catch (checkError) {
        console.error('[FRONTEND DEBUG] checkMessageLimit failed:', {
          error: checkError,
          errorType: typeof checkError,
          errorMessage: checkError instanceof Error ? checkError.message : String(checkError),
          errorStack: checkError instanceof Error ? checkError.stack : 'No stack trace'
        });
        throw new Error(`Failed to check message limit: ${checkError instanceof Error ? checkError.message : String(checkError)}`);
      }
      
      if (!limitCheck.allowed) {
        console.log('[FRONTEND DEBUG] BLOCKING MESSAGE - allowed is false');
        const errorMessage = limitCheck.reason === 'daily_limit_exceeded' 
          ? `Daily limit exceeded (${limitCheck.daily_usage}/${limitCheck.daily_limit})`
          : limitCheck.reason === 'monthly_limit_exceeded'
          ? `Monthly limit exceeded (${limitCheck.monthly_usage}/${limitCheck.monthly_limit})`
          : 'Usage limit exceeded';
        
        toast.error(errorMessage);
        return false;
      }
      
      console.log('[FRONTEND DEBUG] ALLOWING MESSAGE - proceeding with increment');

      // STEP 2: Immediately increment usage in database
      try {
        await incrementMessageUsage(user.id, topic);
        console.log('[FRONTEND DEBUG] incrementMessageUsage completed successfully');
      } catch (incrementError) {
        console.error('[FRONTEND DEBUG] incrementMessageUsage failed:', {
          error: incrementError,
          errorType: typeof incrementError,
          errorMessage: incrementError instanceof Error ? incrementError.message : String(incrementError),
          errorStack: incrementError instanceof Error ? incrementError.stack : 'No stack trace'
        });
        throw new Error(`Failed to increment usage: ${incrementError instanceof Error ? incrementError.message : String(incrementError)}`);
      }
      
      // STEP 3: Trigger UI usage update immediately
      try {
        // Force immediate UI refresh
        window.dispatchEvent(new CustomEvent('ai-usage-updated'));
        console.log('[FRONTEND DEBUG] UI update event dispatched');
      } catch (uiError) {
        console.error('[FRONTEND DEBUG] UI update failed:', uiError);
        // Don't throw here - this is not critical
      }
      
      console.log('[USAGE CHECK] Usage checked and incremented successfully:', {
        topic,
        userId: user.id.substring(0, 8) + '...',
        dailyUsageAfter: limitCheck.daily_usage + 1,
        monthlyUsageAfter: limitCheck.monthly_usage + 1
      });
      
      return true;
      
    } catch (error) {
      // Enhanced error logging with proper error object handling
      console.error('[FRONTEND DEBUG] handleUsageCheck caught error:', {
        error,
        errorType: typeof error,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : 'No stack trace',
        topic,
        userId: user?.id?.substring(0, 8) + '...'
      });
      
      // Ensure we always work with a proper Error object
      const properError = error instanceof Error ? error : new Error(`Usage check failed: ${String(error)}`);
      
      logger.error('Error in usage check', {
        error: properError.message,
        stack: properError.stack,
        topic,
        userId: user?.id
      });
      
      toast.error(`Failed to check usage limits: ${properError.message}`);
      return false;
    }
  }, [user?.id]);

  // Message sending without usage validation (usage already handled)
  const handleSendMessage = useCallback(async (options: MessageOptions) => {
    setIsAutoScrollEnabled(true);
    
    try {
      if (!options || typeof options.content !== 'string' || options.content.trim().length === 0) {
        throw new Error('Invalid message content');
      }

      logger.debug('Sending message (usage already validated)', {
        sessionId: currentSession?.id,
        contentLength: options.content.length,
        stream: options.stream !== false,
        component: 'AiAssistantPage'
      });

      // Send message directly - usage already validated and incremented
      await sendMessage({
        ...options,
        stream: options.stream !== false
      });

      logger.debug('Message sent successfully', {
        sessionId: currentSession?.id,
        component: 'AiAssistantPage'
      });

    } catch (error) {
      logger.error('Error sending message', {
        error: error instanceof Error ? error.message : String(error),
        sessionId: currentSession?.id,
        component: 'AiAssistantPage'
      });
      
      if (error instanceof Error) {
        if (error.message.includes('Invalid message content')) {
          toast.error('Please enter a valid message');
          return;
        } else {
          toast.error('Failed to send message. Please try again.');
        }
      } else {
        toast.error('An unexpected error occurred');
      }
      
      throw error;
    }
  }, [sendMessage, currentSession?.id]);

  const handleRegenerateMessage = useCallback(async (messageId: string) => {
    try {
      const message = messages.find(m => m.id === messageId);
      if (!message) return;

      const messageIndex = messages.indexOf(message);
      const previousUserMessage = messages
        .slice(0, messageIndex)
        .reverse()
        .find(m => m.role === 'user');

      if (previousUserMessage) {
        await handleSendMessage({
          content: previousUserMessage.content,
          includeMultiLanguage: true,
          mode: aiMode,
          stream: true
        });
      }
    } catch (error) {
      console.error('Error regenerating message:', error);
      toast.error('Failed to regenerate message');
    }
  }, [messages, handleSendMessage, aiMode]);

  // Gesture handlers for mobile - Updated to not interfere with scrolling
  const handleSwipeLeft = useCallback(() => {
    if (isMobile) {
      toast("Swipe left detected", { description: "Feature coming soon!" });
    }
  }, [isMobile]);

  const handleSwipeRight = useCallback(() => {
    if (isMobile) {
      toast("Swipe right detected", { description: "Feature coming soon!" });
    }
  }, [isMobile]);

  const handleLongPress = useCallback(() => {
    if (isMobile) {
      toast("Long press detected", { description: "Context menu coming soon!" });
    }
  }, [isMobile]);

  const handleSuggestionClick = useCallback((suggestion: string) => {
    try {
      if (!suggestion || typeof suggestion !== 'string' || suggestion.trim().length === 0) {
        toast.error('Invalid suggestion');
        return;
      }

      setIsAutoScrollEnabled(true);
      
      sendMessage({
        content: suggestion.trim(),
        includeMultiLanguage: true,
        mode: aiMode,
        stream: true
      });
    } catch (error) {
      console.error('Error handling suggestion click:', error);
      toast.error('Failed to send suggestion. Please try again.');
    }
  }, [sendMessage, aiMode]);

  const handleModeToggle = useCallback(() => {
    try {
      const newMode = aiMode === 'expert' ? 'conversation' : 'expert';
      updateAiMode(newMode);
      toast.success(newMode === 'expert' ? 'Switched to Expert Mode' : 'Switched to Conversation Mode');
    } catch (error) {
      console.error('Error toggling AI mode:', error);
      toast.error('Failed to switch mode. Please try again.');
    }
  }, [aiMode, updateAiMode]);

  const handleLoadMoreMessages = useCallback(async () => {
    try {
      const result = await loadMoreMessages();
      if (!result) {
        console.log("No more messages to load or loading failed");
      }
      return result;
    } catch (error) {
      console.error("Error loading more messages:", error);
      toast.error("Failed to load more messages");
      return false;
    }
  }, [loadMoreMessages]);

  const handleSwitchBranch = useCallback((branchId: string) => {
    toast("Branch switching", { description: "Feature coming soon!" });
  }, []);

  const handleCreateBranch = useCallback((fromMessageId?: string) => {
    toast("Create branch", { description: "Feature coming soon!" });
  }, []);

  // Get gradient background based on session topic
  const getGradientClass = useCallback(() => {
    if (!currentSession) return 'bg-gradient-to-b from-background to-background';
    
    switch (currentSession.topic) {
      case 'guide':
        return 'bg-gradient-to-b from-botscheme-royal-purple/10 via-botscheme-royal-purple/5 to-background';
      case 'performance':
        return 'bg-gradient-to-b from-botscheme-ocean-blue/10 via-botscheme-ocean-blue/5 to-background';
      case 'trading_strategy':
        return 'bg-gradient-to-b from-botscheme-emerald-green/10 via-botscheme-emerald-green/5 to-background';
      case 'advisor':
        return 'bg-gradient-to-b from-botscheme-amber-gold/10 via-botscheme-amber-gold/5 to-background';
      default:
        return 'bg-gradient-to-b from-background to-background';
    }
  }, [currentSession]);

  const shouldShowSuggestions = aiMode === 'expert';
  const safeMessageCount = Array.isArray(messages) ? messages.length : 0;

  useEffect(() => {
    console.log('AiAssistantPage scroll state:', {
      isNearBottom,
      isAutoScrollEnabled,
      messagesLength: safeMessageCount
    });
  }, [isNearBottom, isAutoScrollEnabled, safeMessageCount]);

  // CONDITIONAL RENDERING INSTEAD OF EARLY RETURN
  // This ensures all hooks are always called in the same order
  if (!currentSession) {
    return <EmptyState />;
  }

  return (
    <GestureHandler
      {...createGestureProps(handleSwipeLeft, handleSwipeRight, handleLongPress)}
      className="flex flex-col h-full w-full relative"
      allowScroll={true}
    >
      <motion.div 
        className={cn(
          "flex flex-col h-full w-full relative overflow-hidden",
          getGradientClass()
        )}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        style={{
          pointerEvents: 'auto',
          position: 'relative'
        }}
      >
        {/* Enhanced status banner with conversation branching */}
        <div className="flex items-center justify-between px-4 py-2 relative z-[2]">
          <StatusBanner
            isAiWorking={isAiWorking}
            isLoading={isLoading}
            topic={currentSession.topic}
            isOnline={isOnline}
          />
          
          {/* Conversation branching - only show if multiple branches exist */}
          <ConversationBranching
            currentBranch={currentBranch}
            branches={conversationBranches}
            topic={currentSession.topic}
            onSwitchBranch={handleSwitchBranch}
            onCreateBranch={handleCreateBranch}
          />
        </div>

        {/* Removed Usage Limit Indicator - no longer needed in unlimited mode */}

        <MessageContainer
          messages={messages}
          hasMoreMessages={hasMoreMessages}
          isTyping={isTyping}
          isLoading={isLoading}
          isLoadingMoreMessages={isLoadingMoreMessages}
          topic={currentSession.topic}
          onFeedbackChange={updateMessageFeedback}
          onSuggestionClick={handleSuggestionClick}
          onLoadMore={handleLoadMoreMessages}
          showSuggestions={shouldShowSuggestions}
          onScrollNearBottom={handleScrollNearBottom}
          onScrollNearTop={handleScrollNearTop}
          scrollAreaRef={chatScrollRef}
          onRegenerateMessage={handleRegenerateMessage}
          isNearBottom={isNearBottom}
          onScrollToBottom={scrollToBottom}
        />
        
        <InputFooter
          onSendMessage={handleSendMessage}
          onUsageCheck={handleUsageCheck}
          isDisabled={isAiWorking}
          topic={currentSession.topic}
          isOnline={isOnline}
          isExpertMode={aiMode === 'expert'}
          onModeToggle={handleModeToggle}
        />
      </motion.div>
    </GestureHandler>
  );
};

export default AiAssistantPage;
